import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { X, ClipboardList, TriangleAlert, Radio, CheckCircle2, Eye, Loader2 } from "lucide-react";

const fmt = (ts) => new Date(ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });

const Section = ({ title, icon: Icon, color, count, children }) => (
  <div className="border border-[#232838] rounded-lg overflow-hidden">
    <div className="px-3 py-2 bg-[#141824] flex items-center gap-2 font-head text-[11px] font-bold uppercase tracking-wide" style={{ color }}>
      <Icon size={13} /> {title} <span className="ml-auto font-mono">{count}</span>
    </div>
    <div className="divide-y divide-[#1a2130]">{children}</div>
  </div>
);

// One-tap Shift Handoff Briefing for oncoming officers.
export default function ShiftBrief({ open, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (open) { setData(null); client.get("/handoff").then((r) => setData(r.data)).catch(() => {}); } }, [open]);
  if (!open) return null;
  const row = "px-3 py-2 font-mono text-[11px] text-[#E5E7EB]";
  return (
    <div data-testid="shift-brief-panel" className="fixed inset-0 z-[1400] bg-black/75 flex justify-end" onClick={onClose}>
      <div className="w-full sm:max-w-xl h-full bg-[#0d1017] border-l border-[#232838] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0d1017] px-4 py-3 border-b border-[#232838] flex items-center gap-2 z-10">
          <ClipboardList size={17} className="text-[#E0B23C]" />
          <div className="font-head font-bold text-white">SHIFT HANDOFF BRIEFING</div>
          {data && <span className="font-mono text-[10px] text-[#5f7396]">as of {fmt(data.generated_at)}</span>}
          <button data-testid="shift-brief-close" onClick={onClose} className="ml-auto text-[#6B7280] hover:text-white"><X size={17} /></button>
        </div>
        {!data ? (
          <div className="flex items-center justify-center h-40 text-[#8299bd]"><Loader2 className="animate-spin mr-2" size={16} /> Compiling briefing…</div>
        ) : (
          <div className="p-4 space-y-4">
            <Section title="Open Calls" icon={Radio} color="#22D3EE" count={data.open_calls.length}>
              {data.open_calls.map((c) => (
                <div key={c.id} className={row} data-testid={`brief-call-${c.cc_number}`}>
                  <span className="font-bold" style={{ color: c.priority === 1 ? "#EF4444" : "#E0B23C" }}>P{c.priority}</span> CC {c.cc_number} · <span className="text-white">{c.call_type_label}</span>
                  {c.needs_reassignment && <span className="text-[#EF4444] font-bold"> · NEEDS UNITS</span>}
                  <div className="text-[#8299bd]">{c.location?.address || "—"} · {c.status.toUpperCase()} · {(c.assigned_units || []).join(", ") || "no units"}</div>
                </div>
              ))}
              {data.open_calls.length === 0 && <div className={row}>Board is clear.</div>}
            </Section>
            <Section title="Hazard Premises" icon={TriangleAlert} color="#F59E0B" count={data.hazard_premises.length}>
              {data.hazard_premises.map((h) => (
                <div key={h.id} className={row}>
                  <span className="text-white">{h.address}{h.apt ? ` #${h.apt}` : ""}</span>{h.escalate && <span className="text-[#EF4444] font-bold"> · ESCALATE</span>}
                  <div className="text-[#F59E0B]">{h.types.join(" · ")}</div>
                </div>
              ))}
              {data.hazard_premises.length === 0 && <div className={row}>No flagged premises.</div>}
            </Section>
            <Section title="BOLOs (24h)" icon={Eye} color="#A78BFA" count={data.bolos.length}>
              {data.bolos.map((b, i) => <div key={i} className={row}><span className="text-[#8299bd]">{fmt(b.ts)} · {b.from_name}</span><div className="text-white">{b.text}</div></div>)}
              {data.bolos.length === 0 && <div className={row}>No active BOLOs.</div>}
            </Section>
            <Section title="Priority Traffic (12h)" icon={TriangleAlert} color="#EF4444" count={data.priority_traffic.length}>
              {data.priority_traffic.map((m, i) => <div key={i} className={row}><span className="text-[#8299bd]">{fmt(m.ts)} · {m.from_name}</span><div className="text-white">{m.text}</div></div>)}
              {data.priority_traffic.length === 0 && <div className={row}>No priority traffic.</div>}
            </Section>
            <Section title="Closed Last Shift (12h)" icon={CheckCircle2} color="#4ade80" count={data.recently_closed.length}>
              {data.recently_closed.map((c) => (
                <div key={c.id} className={row}>CC {c.cc_number} · <span className="text-white">{c.call_type_label}</span> · <span className="text-[#4ade80] font-bold">{c.disposition_code || "—"}</span><div className="text-[#8299bd]">{c.disposition || ""}</div></div>
              ))}
              {data.recently_closed.length === 0 && <div className={row}>Nothing closed in the last 12 hours.</div>}
            </Section>
            <div className="font-mono text-[10px] text-[#5B6472]">Units on the board: {data.units.filter((u) => u.status !== "off_duty").length} · available {data.units.filter((u) => u.status === "available").length}</div>
          </div>
        )}
      </div>
    </div>
  );
}
